import { centsToDollars } from "@outschool/business-rules";
import {
  ActivityListing_ListingFragment,
  SaveButton_ActivityFragment
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  onboardingQuizPath,
  pathFromLocation
} from "@outschool/routes";
import {
  IntegrationCategory,
  useAnalytics,
  useProductAddedToWishlist,
  useTrackEvent
} from "@outschool/ui-analytics";
import {
  AuthTrigger,
  AuthTriggerActionTypes,
  useLoginFlowContext,
  useSession
} from "@outschool/ui-auth";
import { HeartButton, HeartButtonProps } from "@outschool/ui-components-shared";
import {
  TrackingProperties,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import React from "react";

import AddToCollectionModal from "./AddToCollectionModal";
import {
  saveActivityMutation,
  useSavedActivityMutations
} from "./graphql/mutations";

import type { AddToCollectionModalProps } from "./AddToCollectionModal";

interface SaveButtonProps extends Pick<HeartButtonProps, "disabled" | "sx"> {
  activity: SaveButton_ActivityFragment;
  leader?: ActivityListing_ListingFragment["leader"];
  isSet?: boolean;
  hideLabelOnMobile?: boolean;
  iconOnly?: boolean;
  iconOnlyNoPadding?: boolean;
  onChange?: (_args?: any) => void;
  unsetLabel?: string;
  unfilledColor?: string;
  twoTone?: boolean;
  trackingProperties?: TrackingProperties;
  size?: HeartButtonProps["size"];
  variant?: HeartButtonProps["variant"];
  isOverlayVariant?: boolean;
}

export function SaveButton({
  activity,
  leader,
  size,
  variant,
  isSet,
  hideLabelOnMobile,
  iconOnly,
  iconOnlyNoPadding,
  onChange = (_saved?: boolean) => {},
  unsetLabel,
  unfilledColor,
  sx,
  trackingProperties = {},
  disabled = false,
  twoTone = false,
  isOverlayVariant = false
}: SaveButtonProps) {
  const { enterLoginFlow } = useLoginFlowContext();
  const { isLoggedIn } = useSession();
  const analytics = useAnalytics();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const trackProductAddedToWishlist = useProductAddedToWishlist();
  const [isSaved, setIsSaved] = React.useState(isSet);
  const [addToCollectionModalMode, setAddToCollectionModalMode] =
    React.useState<AddToCollectionModalProps["mode"]>("closed");

  React.useEffect(() => {
    setIsSaved(isSet);
  }, [isSet, setIsSaved]);

  const {
    saveActivity: [saveActivity, savedActivity],
    unsaveActivity: [unsaveActivity, activityIsUnsaved]
  } = useSavedActivityMutations(activity?.uid);

  const saveHeartImpressionNodeRef = React.useRef<HTMLElement | null>(null);
  const { trackTouch: trackSaveHeartTouch } = useImpressionTracking({
    node: saveHeartImpressionNodeRef.current,
    trackingLabel: "save_heart",
    sharedProperties: {
      activity_uid: activity.uid,
      is_saved: isSaved,
      ...trackingProperties
    },
    uniqueId: activity.uid
  });

  React.useEffect(() => {
    if (savedActivity && savedActivity.activity_uid === activity.uid) {
      setIsSaved(true);
    }
  }, [savedActivity, activity?.uid]);

  React.useEffect(() => {
    if (activityIsUnsaved) {
      setIsSaved(false);
    }
  }, [activityIsUnsaved]);

  const handleSaveClick = async (saved: any) => {
    const referer = pathFromLocation(window.location);
    const attribution = await Promise.race([
      analytics.attribution(),
      new Promise(resolve => {
        setTimeout(() => resolve(undefined), 1000);
      })
    ]);
    if (!isLoggedIn) {
      enterLoginFlow({
        authTrigger: AuthTrigger.SAVE_CLASS,
        postLoginAction: {
          actionType: AuthTriggerActionTypes.SAVE_AFTER_LOGIN,
          welcomeModalData: undefined,
          graphqlMutation: {
            mutation: saveActivityMutation,
            variables: {
              activityUid: activity.uid,
              referer,
              attribution
            }
          },
          shouldPopUpWelcomeModal: false
        },
        shouldNavigateOverwrite: false,
        newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
          savedActivityUid: activity.uid
        }),
        newUrlAfterLogin: pathFromLocation(window.location)
      });
    } else {
      if (saved) {
        trackProductAddedToWishlist("class", "favorites", {
          // @ts-ignore TS(2322): Type 'SaveButton_ActivityFragment' is not assignab... Remove this comment to see the full error message
          activity,
          // @ts-ignore TS(2322): Type '{ [key: string]: unknown; name: string; } | ... Remove this comment to see the full error message
          leader
        });
        trackAdEvent("Save Class", {
          products: [
            {
              product_id: activity.slug_id,
              name: activity.title,
              quantity: "1",
              // @ts-ignore TS(2345): Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
              price: centsToDollars(activity.price_cents),
              class_image_url: activity.details.photo,
              class_id: activity.uid
            }
          ]
        });
        await saveActivity(referer, attribution);
        setAddToCollectionModalMode("add");
      } else if (activity?.savedClassLists?.length > 0) {
        setAddToCollectionModalMode("edit");
      } else {
        await unsaveActivity();
      }
    }
    trackSaveHeartTouch({ is_saved: saved });
    onChange(saved);
  };

  const { t } = useTranslation("client\\components\\saving\\SaveButton");

  return (
    <>
      <AddToCollectionModal
        mode={addToCollectionModalMode}
        onDone={() => {
          /* eslint-disable-next-line i18next/no-literal-string */
          setAddToCollectionModalMode("closed");
        }}
        activity={activity}
      />
      <HeartButton
        hideLabelOnMobile={hideLabelOnMobile}
        size={size}
        variant={isOverlayVariant ? "outlined" : variant}
        data-test-id="save-button"
        isSet={!!isSaved}
        setLabel={t`Saved`}
        unsetLabel={unsetLabel || t`Save`}
        onChange={disabled ? null : handleSaveClick}
        iconOnly={iconOnly}
        iconOnlyNoPadding={iconOnlyNoPadding}
        ref={saveHeartImpressionNodeRef as any}
        unfilledColor={unfilledColor}
        sx={isOverlayVariant ? overlayVariantCss : sx}
        disabled={disabled}
        twoTone={twoTone}
      />
    </>
  );
}

const overlayVariantCss = {
  padding: 6,
  border: "2px solid #4B01D4 !important",
  height: "min-content",
  fontSize: "1.1em"
};

/*
 * A heart icon for saving activities.
 * Maintains internal state to reflect changes quickly, before the props have a chance to update.
 */
export function SaveHeart(props: Omit<SaveButtonProps, "simpleHeart">) {
  return <SaveButton iconOnly {...props} />;
}
